// pages/public/PharmacyDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  Star,
  Navigation,
  Truck,
  Shield,
  ChevronLeft,
  Share2,
  Heart,
  Check,
  X,
  Package,
  DollarSign,
  AlertCircle,
  Image as ImageIcon
} from 'lucide-react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import ProductCard from '../../components/cards/ProductCard';
import { pharmacyService } from '../../services/pharmacyService';
import { useAuth } from '../../context/AuthContext';

const PharmacyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  
  const [pharmacy, setPharmacy] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('about');
  const [selectedImage, setSelectedImage] = useState(0);
  const [userLocation, setUserLocation] = useState(null);

  // Fetch pharmacy details
  useEffect(() => {
    const fetchPharmacyDetails = async () => {
      try {
        setLoading(true);
        setError('');

        const response = await pharmacyService.getPharmacyById(id);

        if (response.success) {
          setPharmacy(response.data);
          if (response.data.images && response.data.images.length > 0) {
            setSelectedImage(0);
          }
        } else {
          setError(response.message || 'Pharmacy not found');
        }
      } catch (err) {
        console.error('Error fetching pharmacy:', err);
        setError('Failed to load pharmacy details');
      } finally {
        setLoading(false);
      }
    };

    fetchPharmacyDetails();
  }, [id]);

  // Get user location for distance calculation
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.warn('Location access denied:', error);
        }
      );
    }
  }, []);

  // Fetch products when "Products" tab is active
  useEffect(() => {
    const fetchProducts = async () => {
      if (activeTab === 'products' && pharmacy) {
        try {
          setProductsLoading(true);
          
          const response = await pharmacyService.getPharmacyProducts(id);
          
          if (response.success) {
            setProducts(response.data?.products || []);
          } else {
            console.error('Failed to fetch products:', response.message);
            setProducts([]);
          }
        } catch (err) {
          console.error('Error fetching products:', err);
          setProducts([]);
        } finally {
          setProductsLoading(false);
        }
      }
    };

    fetchProducts();
  }, [activeTab, pharmacy, id]);

  // Calculate distance from user location
  const calculateDistance = () => {
    if (!userLocation || !pharmacy?.location?.coordinates) return null;

    const [pharmLng, pharmLat] = pharmacy.location.coordinates;
    const { latitude, longitude } = userLocation;

    const R = 6371;
    const dLat = (latitude - pharmLat) * Math.PI / 180;
    const dLon = (longitude - pharmLng) * Math.PI / 180;

    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
              Math.cos(pharmLat * Math.PI / 180) * Math.cos(latitude * Math.PI / 180) *
              Math.sin(dLon/2) * Math.sin(dLon/2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    const distance = R * c;

    return distance.toFixed(1);
  };

  // Check if pharmacy is open
  const isPharmacyOpen = () => {
    if (!pharmacy?.operatingHours) return null;

    const now = new Date();
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDay = dayNames[now.getDay()];
    const hours = pharmacy.operatingHours[currentDay];

    if (!hours || !hours.open || !hours.close) return null;

    const currentTime = now.getHours() * 60 + now.getMinutes();
    const [openHour, openMin] = hours.open.split(':').map(Number);
    const [closeHour, closeMin] = hours.close.split(':').map(Number);
    const openTime = openHour * 60 + openMin;
    const closeTime = closeHour * 60 + closeMin;

    return currentTime >= openTime && currentTime <= closeTime;
  };

  // Get directions
  const handleGetDirections = () => {
    if (pharmacy?.location?.coordinates) {
      const [lng, lat] = pharmacy.location.coordinates;
      const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
      window.open(url, '_blank');
    }
  };

  // Share pharmacy
  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: pharmacy.name,
          text: `Check out ${pharmacy.name} on MediGo`,
          url: window.location.href
        });
      } catch (err) {
        console.log('Error sharing:', err);
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Format operating hours
  const formatHours = (day) => {
    const hours = pharmacy?.operatingHours?.[day.toLowerCase()];
    if (!hours || !hours.open || !hours.close) return 'Closed';
    return `${hours.open} - ${hours.close}`;
  };

  // Handle order success (refresh products)
  const handleOrderSuccess = () => {
    // Optionally refresh products list
    if (activeTab === 'products') {
      const fetchProducts = async () => {
        try {
          const response = await pharmacyService.getPharmacyProducts(id);
          if (response.success) {
            setProducts(response.data?.products || []);
          }
        } catch (err) {
          console.error('Error refreshing products:', err);
        }
      };
      fetchProducts();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <LoadingSpinner size="lg" text="Loading pharmacy details..." />
      </div>
    );
  }

  if (error || !pharmacy) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <AlertCircle size={64} className="mx-auto text-red-500 mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Pharmacy Not Found</h2>
          <p className="text-gray-600 mb-6">{error || 'The pharmacy you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/pharmacies')}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Back to Pharmacies
          </button>
        </div>
      </div>
    );
  }

  const distance = calculateDistance();
  const isOpen = isPharmacyOpen();
  const primaryImage = pharmacy.images?.[selectedImage]?.url || '/api/placeholder/800/400';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button
            onClick={() => navigate('/pharmacies')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-4"
          >
            <ChevronLeft size={20} />
            Back to Pharmacies
          </button>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Images */}
            <div>
              <div className="relative rounded-lg overflow-hidden mb-4 bg-gray-200" style={{ height: '400px' }}>
                {pharmacy.images && pharmacy.images.length > 0 ? (
                  <img
                    src={primaryImage}
                    alt={pharmacy.name}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.src = '/api/placeholder/800/400';
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon size={64} className="text-gray-400" />
                  </div>
                )}
                
                {pharmacy.isVerified && (
                  <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-sm flex items-center gap-1">
                    <Shield size={16} />
                    Verified
                  </div>
                )}

                {isOpen !== null && (
                  <div className={`absolute top-4 left-4 px-3 py-1 rounded-full text-sm font-medium ${
                    isOpen ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {isOpen ? '● Open Now' : '● Closed'}
                  </div>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {pharmacy.images && pharmacy.images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {pharmacy.images.map((image, index) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImage(index)}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${
                        selectedImage === index ? 'border-green-500' : 'border-gray-200'
                      }`}
                    >
                      <img
                        src={image.url}
                        alt={`${pharmacy.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.src = '/api/placeholder/100/100';
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div>
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{pharmacy.name}</h1>
                  
                  {pharmacy.rating?.average > 0 && (
                    <div className="flex items-center gap-2 mb-4">
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={20}
                            className={
                              star <= pharmacy.rating.average
                                ? 'fill-yellow-400 text-yellow-400'
                                : 'text-gray-300'
                            }
                          />
                        ))}
                      </div>
                      <span className="text-lg font-semibold">{pharmacy.rating.average.toFixed(1)}</span>
                      <span className="text-gray-500">({pharmacy.rating.count} reviews)</span>
                    </div>
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={handleShare}
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    title="Share"
                  >
                    <Share2 size={20} />
                  </button>
                  <button
                    className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                    title="Save"
                  >
                    <Heart size={20} />
                  </button>
                </div>
              </div>

              {pharmacy.description && (
                <p className="text-gray-600 mb-6">{pharmacy.description}</p>
              )}

              {/* Quick Info */}
              <div className="space-y-3 mb-6">
                <div className="flex items-start gap-3">
                  <MapPin size={20} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-gray-900">Address</p>
                    <p className="text-gray-600">{pharmacy.address}</p>
                    {distance && (
                      <p className="text-sm text-green-600 mt-1">{distance} km away</p>
                    )}
                  </div>
                </div>

                {pharmacy.phone && (
                  <div className="flex items-center gap-3">
                    <Phone size={20} className="text-green-600" />
                    <div>
                      <p className="font-medium text-gray-900">Phone</p>
                      <a href={`tel:${pharmacy.phone}`} className="text-green-600 hover:underline">
                        {pharmacy.phone}
                      </a>
                    </div>
                  </div>
                )}

                {pharmacy.email && (
                  <div className="flex items-center gap-3">
                    <Mail size={20} className="text-green-600" />
                    <div>
                      <p className="font-medium text-gray-900">Email</p>
                      <a href={`mailto:${pharmacy.email}`} className="text-green-600 hover:underline">
                        {pharmacy.email}
                      </a>
                    </div>
                  </div>
                )}
              </div>

              {/* Features */}
              <div className="flex flex-wrap gap-2 mb-6">
                {pharmacy.deliveryService && (
                  <span className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-sm font-medium">
                    <Truck size={14} />
                    Delivery Available
                  </span>
                )}
                {pharmacy.isVerified && (
                  <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-3 py-1 rounded-full text-sm font-medium">
                    <Shield size={14} />
                    Licensed
                  </span>
                )}
                {pharmacy.deliveryFee === 0 && pharmacy.deliveryService && (
                  <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 px-3 py-1 rounded-full text-sm font-medium">
                    <DollarSign size={14} />
                    Free Delivery
                  </span>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-3">
                {pharmacy.phone && (
                  <a
                    href={`tel:${pharmacy.phone}`}
                    className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <Phone size={20} />
                    Call Now
                  </a>
                )}
                <button
                  onClick={handleGetDirections}
                  className="flex-1 min-w-[200px] flex items-center justify-center gap-2 px-6 py-3 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 transition-colors"
                >
                  <Navigation size={20} />
                  Get Directions
                </button>
              </div>

              {/* Delivery Info */}
              {pharmacy.deliveryService && (
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Truck size={20} className="text-blue-600" />
                    Delivery Information
                  </h3>
                  <div className="space-y-1 text-sm text-gray-600">
                    <p>• Delivery within {pharmacy.deliveryRadius || 5} km radius</p>
                    <p>• Delivery fee: KES {pharmacy.deliveryFee || 0}</p>
                    <p>• Estimated delivery: 30-60 minutes</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('about')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'about'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              About
            </button>
            <button
              onClick={() => setActiveTab('hours')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'hours'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Operating Hours
            </button>
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'products'
                  ? 'border-green-600 text-green-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Products
            </button>
          </div>
        </div>
      </div>

      {/* Tab Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'about' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">About {pharmacy.name}</h2>
            
            {pharmacy.description ? (
              <p className="text-gray-600 mb-6">{pharmacy.description}</p>
            ) : (
              <p className="text-gray-500 italic mb-6">No description available.</p>
            )}

            {pharmacy.licenseNumber && (
              <div className="mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">License Information</h3>
                <p className="text-gray-600">License Number: {pharmacy.licenseNumber}</p>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Features</h3>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    {pharmacy.deliveryService ? (
                      <Check size={16} className="text-green-600" />
                    ) : (
                      <X size={16} className="text-gray-400" />
                    )}
                    <span className={pharmacy.deliveryService ? 'text-gray-900' : 'text-gray-500'}>
                      Home Delivery
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    {pharmacy.isVerified ? (
                      <Check size={16} className="text-green-600" />
                    ) : (
                      <X size={16} className="text-gray-400" />
                    )}
                    <span className={pharmacy.isVerified ? 'text-gray-900' : 'text-gray-500'}>
                      Verified & Licensed
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'hours' && (
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
              <Clock size={24} />
              Operating Hours
            </h2>
            
            <div className="space-y-3">
              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day) => (
                <div key={day} className="flex justify-between items-center py-3 border-b border-gray-200 last:border-0">
                  <span className="font-medium text-gray-900">{day}</span>
                  <span className="text-gray-600">{formatHours(day)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'products' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Package size={24} />
                Available Products
              </h2>
              
              {/* Login prompt for non-authenticated users */}
              {!isAuthenticated && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2">
                  <p className="text-sm text-blue-800">
                    <a href="/auth/login" className="font-medium underline hover:text-blue-900">
                      Login
                    </a> to place orders
                  </p>
                </div>
              )}
            </div>
            
            {productsLoading ? (
              <div className="flex justify-center py-12">
                <LoadingSpinner text="Loading products..." />
              </div>
            ) : products.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {products.map((product) => (
                  <ProductCard
                    key={product._id}
                    product={product}
                    pharmacy={pharmacy}
                    user={isAuthenticated ? user : null}
                    onOrderSuccess={handleOrderSuccess}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <Package size={64} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-500">No products available at the moment</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default PharmacyDetail;